/**
 * The API's answer to "this module is off for your school" is
 * `403 { error: { code: "MODULE_NOT_IN_PLAN" } }`. `api/client.ts` reports it
 * here instead of importing react-query (which would make the HTTP client
 * depend on the cache that depends on it): `hooks/use-module-access.ts`
 * subscribes, refetches `/me/entitlements` (so the menu, dock and cards catch
 * up and the module disappears) and this file shows ONE calm toast rather than
 * leaving an empty card behind.
 */
import { toast } from "sonner";

export const MODULE_NOT_IN_PLAN_CODE = "MODULE_NOT_IN_PLAN";
export const MODULE_NOT_ENABLED_MESSAGE = "This feature isn't enabled for your school";

type Listener = () => void;
const listeners = new Set<Listener>();
let lastReportAt = 0;
/** One refetch / toast per burst: a dashboard can fire a dozen 403s at once. */
const REPORT_WINDOW_MS = 5_000;

export function onModuleNotInPlan(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Is this axios-style error body a MODULE_NOT_IN_PLAN 403? */
export function isModuleNotInPlanBody(status: number | undefined, data: unknown): boolean {
  if (status !== 403) return false;
  const code = (data as { error?: { code?: unknown } } | null | undefined)?.error?.code;
  return code === MODULE_NOT_IN_PLAN_CODE;
}

export function reportModuleNotInPlan(opts: { toast?: boolean; now?: number } = {}): boolean {
  const now = opts.now ?? Date.now();
  if (now - lastReportAt < REPORT_WINDOW_MS) return false;
  lastReportAt = now;
  if (opts.toast !== false) toast.info(MODULE_NOT_ENABLED_MESSAGE, { id: "module-not-in-plan" });
  listeners.forEach((l) => l());
  return true;
}

/** Test seam. */
export function resetModuleNotInPlanThrottle(): void {
  lastReportAt = 0;
}
